import { useState } from "react";
import { Share2, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { withTrace } from "@/hooks/useTraceSection";

interface WhatsAppShareSectionProps {
  invitationText: string;
  onInvitationTextChange: (text: string) => void;
  shareUrl: string;
  loading?: boolean;
  eventUuid?: string;
}

export function WhatsAppShareSection({
  invitationText,
  onInvitationTextChange,
  shareUrl,
  loading = false,
  eventUuid,
}: WhatsAppShareSectionProps) {
  const { t } = useTranslation();
  const [whatsappDropdownOpen, setWhatsappDropdownOpen] = useState(false);

  const handleCopyWhatsappMessage = async () => {
    if (!invitationText) return;

    await withTrace("shareModal", "copyWhatsappMessage", async (span) => {
      span.setAttribute("event.uuid", eventUuid ?? "");
      span.setAttribute("share.invitation.length", invitationText.length);
      try {
        await navigator.clipboard.writeText(invitationText);
        toast.success(t("events.share.whatsappCopied"));
      } catch (error) {
        const err = error as Error;
        span.recordException(err);
        console.error("Failed to copy WhatsApp message:", err);
        toast.error(t("events.share.whatsappCopyError"));
      }
    }, {
      eventUuid: eventUuid ?? "",
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs font-medium">
        <Share2 className="h-4 w-4" />
        <span>{t("events.share.whatsapp")}</span>
      </div>
      <div className="space-y-2">
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleCopyWhatsappMessage}
            disabled={!invitationText || loading}
            className="flex-1 justify-center text-sm"
          >
            <Share2 className="mr-2 h-4 w-4" />
            <span className="text-xs">{t("events.share.copyWhatsappMessage")}</span>
          </Button>
          <div data-state={whatsappDropdownOpen ? "open" : "closed"}>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setWhatsappDropdownOpen(!whatsappDropdownOpen)}
              className="shrink-0"
              aria-label={t("events.share.editWhatsappMessage")}
              type="button"
              aria-controls="whatsapp-textarea"
              aria-expanded={whatsappDropdownOpen}
            >
              <ChevronDown className={cn("h-4 w-4 transition-transform", whatsappDropdownOpen && "rotate-180")} />
            </Button>
          </div>
        </div>
        {/* WhatsApp Textarea (Expandable) */}
        {whatsappDropdownOpen && (
          <div className="space-y-2" data-state="open" id="whatsapp-textarea">
            <Textarea
              value={invitationText}
              onChange={(e) => onInvitationTextChange(e.target.value)}
              placeholder={t("events.share.whatsappMessagePlaceholder", {
                url: shareUrl,
              })}
              className="min-h-[100px] text-sm resize-none"
              disabled={loading}
            />
          </div>
        )}
      </div>
    </div>
  );
}

